import React, { useState, useEffect, useMemo } from "react";
import { Card, Col, Row } from "react-bootstrap";
import { toast } from "react-toastify";
import Button from "components/base/Button";
import ProductVariant from "components/forms/ProductVariant";
import { Attribute } from "components/products/types";
import Skeleton from "react-loading-skeleton"; // Import Skeleton loader
import { useTranslation } from "react-i18next";

interface VariantFormCardProps {
  className?: string;
  variationAttributes: Attribute[];
  handleChange: (attribute_code: string, value: any) => void;
  formValues: { [key: string]: any };
  onCreateVariants: (variants: any[], selectedAttributes: Attribute[]) => void;
  onRemoveVariant: (index: number) => void;
  isLoadingAttributes: boolean; // Add this prop to control the loader
  validationError?: string | null; // Added prop for validation error message
}

const VariantFormCard: React.FC<VariantFormCardProps> = ({
  className,
  variationAttributes,
  handleChange,
  formValues,
  onCreateVariants,
  onRemoveVariant,
  isLoadingAttributes,
  validationError
}) => {
  const { t } = useTranslation();
  const [variants, setVariants] = useState<string[]>([""]);
  const [selectedAttributes, setSelectedAttributes] = useState<Attribute[]>([]);
  const [invalidFields, setInvalidFields] = useState<string[]>([]);

  const maxVariants = 2;

  // Log variation attributes when they change to help with debugging
  useEffect(() => {
    console.log("Variation attributes updated:", variationAttributes);
  }, [variationAttributes]);
  
  // Only clear validation errors when specific fields change
  useEffect(() => {
    // Reset errors only if actual attribute or option selections change
    if (formValues.variantAttribute_0 || formValues.selectedAttributeOptions_0?.length || 
        formValues.variantAttribute_1 || formValues.selectedAttributeOptions_1?.length) {
      const fieldsToCheck = [
        'variantAttribute_0', 'selectedAttributeOptions_0',
        'variantAttribute_1', 'selectedAttributeOptions_1'
      ];
      
      // Only clear errors for fields that have been populated
      setInvalidFields(prev => 
        prev.filter(field => {
          // If this field is now populated, remove it from invalid fields
          if (field === 'variantAttribute_0' && formValues.variantAttribute_0) return false;
          if (field === 'selectedAttributeOptions_0' && formValues.selectedAttributeOptions_0?.length) return false;
          if (field === 'variantAttribute_1' && formValues.variantAttribute_1) return false;
          if (field === 'selectedAttributeOptions_1' && formValues.selectedAttributeOptions_1?.length) return false;
          return true;
        })
      );
    }
  }, [formValues.variantAttribute_0, formValues.selectedAttributeOptions_0, 
      formValues.variantAttribute_1, formValues.selectedAttributeOptions_1]);

  useEffect(() => {
    // Initialize selected attributes based on formValues
    const attribute_0 = variationAttributes.find(
      (attr) =>
        attr.attribute_code === formValues.variantAttribute_0?.attribute_code
    );
    const attribute_1 = variationAttributes.find(
      (attr) =>
        attr.attribute_code === formValues.variantAttribute_1?.attribute_code
    );
    if (attribute_0) setSelectedAttributes([attribute_0]);
    if (attribute_1) setSelectedAttributes((prev) => [...prev, attribute_1]);

    // If variantAttribute_1 exists, initialize with two variants
    if (formValues.variantAttribute_1) {
      setVariants(["", ""]);
    }
  }, [formValues, variationAttributes]);

  const handleAddVariant = () => {
    if (variants.length < maxVariants) {
      setVariants([...variants, ""]);
      setSelectedAttributes([...selectedAttributes, {} as Attribute]);
    }
  };

  const handleRemoveVariant = (index: number) => {
    // Update the local state and selected attributes
    setVariants((prevVariants) => prevVariants.filter((_, i) => i !== index));
    setSelectedAttributes((prevAttributes) =>
      prevAttributes.filter((_, i) => i !== index)
    );
    onRemoveVariant(index);
  };

  const handleAttributeChange = (
    index: number,
    input_code: string,
    value: any
  ) => {
    // set the selected attribute for the current variant
    const newSelectedAttributes = [...selectedAttributes];
    const selectedAttribute = variationAttributes.find(
      (attr) => attr.attribute_code === value.attribute_code
    );
    if (selectedAttribute) {
      newSelectedAttributes[index] = selectedAttribute;
    }
    setSelectedAttributes(newSelectedAttributes);

    handleChange(input_code, value);
  };

  const getFilteredAttributes = (index: number): Attribute[] => {
    if (index === 1 && selectedAttributes[0]) {
      return variationAttributes.filter(
        (attr) => attr.attribute_code !== selectedAttributes[0].attribute_code
      );
    } else if (index === 0 && selectedAttributes[1]) {
      return variationAttributes.filter(
        (attr) => attr.attribute_code !== selectedAttributes[1].attribute_code
      );
    }
    return variationAttributes;
  };

  const handleCreateVariants = () => {
    // We'll only check variant-specific fields if our hook indicates all other
    // required fields are fine
    const selectedOptions0 = formValues.selectedAttributeOptions_0 || [];
    const selectedOptions1 = formValues.selectedAttributeOptions_1 || [];
    const newInvalidFields: string[] = [];
    
    // Validate attribute selection
    if (!formValues.variantAttribute_0) {
      newInvalidFields.push('variantAttribute_0');
    }
    
    // Validate option selection for first attribute
    if (selectedOptions0.length === 0) {
      newInvalidFields.push('selectedAttributeOptions_0');
    } else if (selectedOptions0.length < 2 && (!formValues.variantAttribute_1 || selectedOptions1.length < 2)) {
      // If we only have one attribute or the second attribute doesn't have enough options,
      // the first attribute must have at least two options
      newInvalidFields.push('selectedAttributeOptions_0');
    }
    
    // Validate second attribute if present
    if (variants.length > 1) {
      if (!formValues.variantAttribute_1) {
        newInvalidFields.push('variantAttribute_1');
      }
      
      if (selectedOptions1.length === 0) {
        newInvalidFields.push('selectedAttributeOptions_1');
      } else if (selectedOptions1.length < 2 && selectedOptions0.length < 2) {
        // If the first attribute doesn't have enough options,
        // the second attribute must have at least two options
        newInvalidFields.push('selectedAttributeOptions_1');
      }
    }
    
    // Set invalid fields in state
    setInvalidFields(newInvalidFields);
    
    // Check if there are validation errors
    if (newInvalidFields.length > 0) {
      // Note: We don't show toast here - the useProductForm hook handles showing errors only
      // if required fields in the main form are all valid
      return;
    }

    // Generate variants from selected attributes and options
    const generatedVariants = combineSelectedOptions(selectedAttributes);
    
    // Debug log to help determine what variants were generated
    console.log("Generated Variants before processing:", generatedVariants);
    
    // Map the generated variants to add default quantity and price
    const processedVariants = generatedVariants.map(variant => ({
      ...variant,
      id: Math.random().toString(36).substr(2, 9), // Add unique ID for table operations
      quantity: formValues.quantity || 1,
      price: formValues.price || 1,
    }));
  
    console.log("Generated Variants after processing:", processedVariants);
  
    // Collect the actual attribute details for the selected attributes
    const actualAttributes: Attribute[] = [];
    
    // Make sure we include the first attribute if it exists
    if (formValues.variantAttribute_0) {
      const attr0 = variationAttributes.find(
        attr => attr.attribute_code === formValues.variantAttribute_0.attribute_code
      );
      if (attr0) {
        actualAttributes.push(attr0);
      }
    }
    
    // Include the second attribute if it exists
    if (formValues.variantAttribute_1) {
      const attr1 = variationAttributes.find(
        attr => attr.attribute_code === formValues.variantAttribute_1.attribute_code
      );
      if (attr1) {
        actualAttributes.push(attr1);
      }
    }
    
    console.log("Actual attributes being passed:", actualAttributes);
    
    // Pass the processed variants to the parent component with the ACTUAL attributes
    onCreateVariants(processedVariants, actualAttributes);
  };    

  const combineSelectedOptions = (attributes: Attribute[]) => {
    console.log("Combining options for attributes:", attributes);
    
    // We need to handle the case where the variantAttribute_0 exists in formValues
    // but didn't make it into the attributes array (because of how useState initializes)
    const attrFromForm0 = formValues.variantAttribute_0;
    const attrFromForm1 = formValues.variantAttribute_1;
    
    // If we have attributes from the form but they're not in our attributes array,
    // use the attributes from the form
    let finalAttributes = [...attributes];
    if (finalAttributes.length === 0 && attrFromForm0) {
      finalAttributes = [attrFromForm0];
      if (attrFromForm1) {
        finalAttributes.push(attrFromForm1);
      }
    }
    
    const selectedOptions0 = formValues.selectedAttributeOptions_0 || [];
    const selectedOptions1 = formValues.selectedAttributeOptions_1 || [];
    
    console.log("Using attributes:", finalAttributes);
    console.log("Selected options 0:", selectedOptions0);
    console.log("Selected options 1:", selectedOptions1);
  
    const allVariants: any[] = [];
    
    // If we have no attributes or no selected options, return empty array
    if (finalAttributes.length === 0 || (selectedOptions0.length === 0 && selectedOptions1.length === 0)) {
      console.log("No attributes or options, returning empty array");
      return [];
    }
  
    if (finalAttributes.length === 1 || selectedOptions1.length === 0) {
      // Only process the first attribute if we only have one attribute or no options for the second
      console.log("Processing single attribute variants");
      selectedOptions0.forEach((option0: any) => {
        allVariants.push({
          [finalAttributes[0].attribute_code]: option0,
          quantity: formValues[`variant_quantity_${option0.option_id}`] || 0,
          price: parseFloat(formValues[`variant_price_${option0.option_id}`]) || 0,
        });
      });
    } else if (finalAttributes.length >= 2 && selectedOptions1.length > 0) {
      // Process both attributes
      console.log("Processing double attribute variants");
      selectedOptions0.forEach((option0: any) => {
        selectedOptions1.forEach((option1: any) => {
          allVariants.push({
            [finalAttributes[0].attribute_code]: option0,
            [finalAttributes[1].attribute_code]: option1,
            quantity:
              formValues[`variant_quantity_${option0.option_id}_${option1.option_id}`] || 0,
            price:
              parseFloat(
                formValues[`variant_price_${option0.option_id}_${option1.option_id}`]
              ) || 0,
          });
        });
      });
    }
    
    console.log("Generated variants:", allVariants);
    return allVariants;
  };  

  return (
    <Card className={className}>
      <Card.Body>
        <h4 className="mb-4">{t("variantsTitle")}</h4>

        {isLoadingAttributes ? (
          <Skeleton
            count={variants.length}
            className="border-bottom border-translucent border-dashed border-sm-0 border-bottom-xl pb-4"
            baseColor="var(--phoenix-secondary-bg)"
            highlightColor="var(--phoenix-emphasis-bg)"
          /> // Show Skeleton while loading
        ) : (
          <Row className="gx-3 gy-4 mb-3">
            {variants.map((variant, index) => (
              <Col xs={12} sm={6} xl={12} key={index}>
                <ProductVariant
                  className="border-bottom border-translucent border-dashed border-sm-0 border-bottom-xl pb-4"
                  index={index}
                  options={getFilteredAttributes(index)}
                  onRemove={() => handleRemoveVariant(index)}
                  handleChange={(input_code, value) =>
                    handleAttributeChange(index, input_code, value)
                  }
                  formValues={formValues}
                  disableRemoveButton={variants.length <= 1} // Disable remove button if there's only one option
                  invalidFields={invalidFields} // Pass invalid fields to ProductVariant
                />
              </Col>
            ))}
          </Row>
        )}

        <Button
          variant="phoenix-primary"
          className="w-100 mb-2"
          onClick={handleAddVariant}
          disabled={
            variants.length >= maxVariants || variationAttributes.length <= 1
          }
        >
          {t("addAnotherOption")}
        </Button>

        {/* Error message removed, will use field highlighting instead */}

        <Button
          variant="phoenix-primary"
          className="w-100 mt-2"
          onClick={() => {
            // Instead of passing empty data, we should call our local handleCreateVariants
            // which will do the validation and generate variants if valid
            handleCreateVariants();
          }}
        >
          {t("createVariants")}
        </Button>
      </Card.Body>
    </Card>
  );
};

export default VariantFormCard;
